import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Heart, MessageCircle, Trophy, TrendingUp, Sparkles, ThumbsUp } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ProgressUpdate {
  id: string;
  user_id: string;
  share_type: string;
  share_data: any;
  visibility: string;
  created_at: string;
  profiles?: {
    display_name: string;
  };
  reaction_count?: number;
  user_reacted?: boolean;
}

export function ProgressFeed() {
  const queryClient = useQueryClient();
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: updates, isLoading } = useQuery({
    queryKey: ['progress-feed', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get user's partner IDs
      const { data: partnerships } = await supabase
        .from('accountability_partnerships')
        .select('user_id, partner_id')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .eq('status', 'active');

      if (!partnerships || partnerships.length === 0) return [];

      const partnerIds = partnerships
        .map(p => p.user_id === user.id ? p.partner_id : p.user_id);

      // Get shared progress from partners
      const { data, error } = await supabase
        .from('shared_progress')
        .select('*')
        .in('user_id', [...partnerIds, user.id])
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      // Fetch profiles separately
      const userIds = [...new Set(data.map(d => d.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);
      
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]));
      
      // Get reaction counts
      const progressIds = data.map(d => d.id);
      const { data: reactions } = await supabase
        .from('progress_reactions')
        .select('progress_id, user_id')
        .in('progress_id', progressIds);
      
      const reactionCounts = new Map<string, number>();
      const userReactions = new Set<string>();
      
      reactions?.forEach(r => {
        reactionCounts.set(r.progress_id, (reactionCounts.get(r.progress_id) || 0) + 1);
        if (r.user_id === user.id) {
          userReactions.add(r.progress_id);
        }
      });
      
      return data.map(d => ({
        ...d,
        profiles: profileMap.get(d.user_id),
        reaction_count: reactionCounts.get(d.id) || 0,
        user_reacted: userReactions.has(d.id),
      })) as ProgressUpdate[];
    },
    enabled: !!user?.id,
  });

  const reactMutation = useMutation({
    mutationFn: async ({ progressId, remove }: { progressId: string; remove?: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");

      if (remove) {
        const { error } = await supabase
          .from('progress_reactions')
          .delete()
          .eq('progress_id', progressId)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('progress_reactions')
          .insert({
            progress_id: progressId,
            user_id: user.id,
            reaction_type: 'like',
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progress-feed'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to react");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading feed...</p>
        </CardContent>
      </Card>
    );
  }

  if (!updates || updates.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              No progress updates yet. Share your wins with your accountability partners!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getShareIcon = (type: string) => {
    if (type === 'challenge_completed') return <Trophy className="h-4 w-4 text-primary" />;
    if (type === 'goal_achieved') return <TrendingUp className="h-4 w-4 text-secondary" />;
    return <Heart className="h-4 w-4 text-secondary" />;
  };

  const getShareTitle = (update: ProgressUpdate) => {
    if (update.share_type === 'challenge_completed') {
      return `Completed ${update.share_data?.challenge_name || 'a challenge'}!`;
    }
    if (update.share_type === 'goal_achieved') {
      return `Achieved goal: ${update.share_data?.goal_name || 'a goal'}`;
    }
    if (update.share_type === 'milestone') {
      return `Reached milestone: ${update.share_data?.milestone_name || 'a milestone'}`;
    }
    return 'Shared an update';
  };

  return (
    <div className="grid gap-4">
      {updates.map((update) => (
        <Card key={update.id}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Avatar>
                <AvatarFallback>
                  {update.profiles?.display_name?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{update.profiles?.display_name || 'Unknown'}</p>
                  {getShareIcon(update.share_type)}
                  <Badge variant="outline" className="text-xs capitalize">
                    {update.share_type.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-sm font-medium">{getShareTitle(update)}</p>
                {update.share_data?.message && (
                  <p className="text-sm text-muted-foreground">{update.share_data.message}</p>
                )}
                <div className="flex items-center gap-4 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => reactMutation.mutate({ 
                      progressId: update.id, 
                      remove: update.user_reacted 
                    })}
                    disabled={reactMutation.isPending}
                    className={`gap-1 h-8 px-2 ${update.user_reacted ? 'text-secondary' : 'text-muted-foreground'}`}
                  >
                    <Heart className={`h-4 w-4 ${update.user_reacted ? 'fill-current' : ''}`} />
                    <span className="text-sm">{update.reaction_count || 0}</span>
                  </Button>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {new Date(update.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
